import { createLogic } from "redux-logic";
import cuid from "cuid";
import {
    type AddToQueueType,
    type FinishTransactionType,
    type OrigActionType,
    type RemoveFromQueueType,
    type StartTransactionType,
    addToQueue,
    finishTransaction,
    removeFromQueue,
    startTransaction,
    TRANSACTION_ADD_TO_QUEUE,
    TRANSACTION_FINISH,
    TRANSACTION_REMOVE_FROM_QUEUE,
    TRANSACTION_START,
} from "../../../../../common-deprecated/redux/actions/TransactionActions";
import { invalidateConfigSave, startConfigSave } from "../actions/SaveActions";
import {
    ADD_EXTRA,
    ADD_FINANCE_PACK,
    ADD_FINANCE_PACK_EXTRAS,
    ADD_FINANCE_PACK_SERVICEPRODUCTS,
    ADD_PACK,
    ADD_PACK_EXTRA,
    ADD_SERVICE_PRODUCT,
    ADD_USER_SELECTED_PROMOTION,
    calculatePrice,
    LOAD_USER_SELECTED,
    POP_BASKET_HISTORY,
    pushHistory,
    REMOVE_EXTRA,
    REMOVE_FINANCE_PACK_EXTRAS,
    REMOVE_FINANCE_PACK_SERVICEPRODUCTS,
    REMOVE_PACK,
    REMOVE_PACK_EXTRA,
    REMOVE_SERVICE_PRODUCT,
    REMOVE_USER_SELECTED_PROMOTION,
    REMOVE_WINTER_WHEEL,
    RESOLVE_CONFLICT_PACK,
    RESOLVE_EXTRA_CONFLICTS,
    SET_COLOUR,
    SET_EXTRA_OPTION,
    SET_PRIMARY_ACCENT_COLOUR,
    SET_SECONDARY_ACCENT_COLOUR,
    SET_UPHOLSTERY,
    SET_WHEEL,
    SET_WINTER_WHEEL,
} from "../actions/BasketActions";
import { invalidateEta, startEtaLoad } from "../actions/EtaActions";
import { hideOverlayLoader, showOverlayLoader } from "../../../../../common-deprecated/redux/actions/UtilActions";
import { startMassManagementFetch } from "../actions/MassManagementActions";
import type { BuildAndBuyLogicType } from "../store";
import { invalidateSpecs } from "../actions/SpecActions";
import { getLocalStorageParams, setUnfinishedConfiguration } from "../../utils/localStorage";
import { getExternalConfigurationObject } from "../../utils/typedUtils";
import { loadMassManagement } from "../../utils/settings/bnbSettingUtils";
import { isLexus } from "../../../../../common-deprecated/settings/utils/commonSettingUtils";
import Navigation, { NavStep } from "../../../shared-logic/utils/Navigation";

/**
 * This logic "catches" actions that or belong to or start with a transaction.
 * If transactions are ever needed outside of basket actions, be sure to update the type regex.
 */
const catchTransactionLogic = (createLogic as BuildAndBuyLogicType<OrigActionType>)({
    type: /^basket\/(?!calculatePrice).*/,
    name: "catchTransactionLogic",

    validate({ getState, action }, allow, reject) {
        const { transaction } = getState();
        if (action.transaction && transaction.cancelled[action.transaction as string]) {
            (reject as () => void)();
        } else if (action.transaction && action.transaction === true) {
            reject(startTransaction(action));
        } else if (action.transaction && !action.actionId) {
            reject(addToQueue(action.transaction, action));
        } else {
            allow(action);
        }
    },

    process({ action }, dispatch, done) {
        if (action.transaction) {
            dispatch(removeFromQueue(action));
        }
        done();
    },
});

const startTransactionLogic = (createLogic as BuildAndBuyLogicType<StartTransactionType>)({
    type: TRANSACTION_START,

    transform({ getState, action }, next) {
        const { origAction } = action;
        origAction.transaction = cuid();

        const transactionAction = {
            action: origAction.type,
            actionQueue: [],
        };

        action.transactionAction = transactionAction;
        action.origAction._snapshot = { ...getState().basket };
        next(action);
    },

    process({ action }, dispatch, done) {
        dispatch(showOverlayLoader());
        dispatch(action.origAction);
        done();
    },
});

const queueLogic = (createLogic as BuildAndBuyLogicType<AddToQueueType>)({
    type: TRANSACTION_ADD_TO_QUEUE,

    transform({ action }, next) {
        action.origAction.actionId = cuid();
        next(action);
    },

    process({ action }, dispatch, done) {
        dispatch(action.origAction);
        done();
    },
});

const removeQueueLogic = (createLogic as BuildAndBuyLogicType<RemoveFromQueueType>)({
    type: TRANSACTION_REMOVE_FROM_QUEUE,

    process({ getState, action }, dispatch, done) {
        const { transaction } = getState();
        const activeTransaction = transaction.active[action.origAction.transaction as string];

        if (activeTransaction && activeTransaction.actionQueue.length === 0) {
            dispatch(finishTransaction(action.origAction.transaction as string));
        }
        done();
    },
});

const finishLogic = (createLogic as BuildAndBuyLogicType<FinishTransactionType>)({
    type: TRANSACTION_FINISH,

    process({ getState, action }, dispatch, done) {
        const {
            transaction,
            navigation,
            externalPrice,
            bnbSettings,
            commonSettings,
            car,
            basket,
            items,
            finance,
            utils,
            tradeInAemExternalData,
        } = getState();
        const finishedType = action.transactionId ? transaction.finished[action.transactionId].action : "";

        dispatch(hideOverlayLoader());

        const updateBasketTypes = [
            SET_COLOUR,
            SET_UPHOLSTERY,
            SET_PRIMARY_ACCENT_COLOUR,
            SET_SECONDARY_ACCENT_COLOUR,
            ADD_PACK_EXTRA,
            REMOVE_PACK_EXTRA,
            LOAD_USER_SELECTED,
            ADD_EXTRA,
            REMOVE_EXTRA,
            ADD_PACK,
            ADD_SERVICE_PRODUCT,
            REMOVE_SERVICE_PRODUCT,
            REMOVE_PACK,
            RESOLVE_CONFLICT_PACK,
            SET_WHEEL,
            SET_EXTRA_OPTION,
            RESOLVE_EXTRA_CONFLICTS,
            SET_WINTER_WHEEL,
            REMOVE_WINTER_WHEEL,
            ADD_USER_SELECTED_PROMOTION,
            REMOVE_USER_SELECTED_PROMOTION,
            ADD_FINANCE_PACK,
            ADD_FINANCE_PACK_EXTRAS,
            REMOVE_FINANCE_PACK_EXTRAS,
            ADD_FINANCE_PACK_SERVICEPRODUCTS,
            REMOVE_FINANCE_PACK_SERVICEPRODUCTS,
        ] as string[];

        if (!finishedType || updateBasketTypes.includes(finishedType)) {
            dispatch(pushHistory());
            dispatch(hideOverlayLoader());

            // ------------------------------------------------------
            // The unfinished configuration is updated/added to localstorage here as this is the only place that is triggered when changing config.
            // ------------------------------------------------------
            if (navigation.step !== NavStep.Summary) {
                const config = getExternalConfigurationObject(
                    commonSettings,
                    car,
                    basket,
                    items,
                    tradeInAemExternalData,
                    false,
                    finance.quotes,
                    utils.financeOption,
                    externalPrice.co2Specs.value,
                );

                const localstorageParams = getLocalStorageParams(commonSettings);

                setUnfinishedConfiguration(localstorageParams, config);
            }

            // Reload ETA info and re-save config if we're on the summary step.
            if (navigation.step === NavStep.Summary) {
                dispatch(invalidateSpecs());
                dispatch(startConfigSave());
            } else if (
                Navigation.getStepOrderIndex(navigation.step, isLexus(commonSettings)) >=
                Navigation.getStepOrderIndex(NavStep.Customize, isLexus(commonSettings))
            ) {
                // Invalidate happens on customize+ because summary can be accessed from customize+ via buy step
                dispatch(invalidateSpecs());
                dispatch(invalidateEta());
                dispatch(invalidateConfigSave());
            }
        }

        if (!finishedType || updateBasketTypes.includes(finishedType) || finishedType === POP_BASKET_HISTORY) {
            dispatch(calculatePrice());

            // If externalPrice is enabled, mass management fetch is part of that same API call. See ExternalPriceLogic.
            if (!externalPrice.enabled && loadMassManagement(bnbSettings)) dispatch(startMassManagementFetch());
            if (navigation.step === NavStep.Summary) dispatch(startEtaLoad());
        }

        done();
    },
});

export default [catchTransactionLogic, startTransactionLogic, queueLogic, removeQueueLogic, finishLogic];
